'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef } from 'react';
import { useSession } from '@/lib/auth-client';
import { useAddVote, useRemoveVote } from '@/lib/hooks';
import { useObjectActivities } from '@/lib/hooks/useObjectActivities';
import { useEngagementSummary } from '@/lib/hooks/useEngagementSummary';
import { Hand, Pencil } from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { StructuredInterestDialog } from './StructuredInterestDialog';
import { useSignInModal } from '@/lib/ui';
import type { StructuredInterestConfig } from '@sizzlenz/shared';
type InterestMode = 'simple' | 'structured';
interface InterestedButtonProps {
  objectId: string;
  objectType: 'item' | 'collection' | 'entity' | 'collection_item' | 'initiative' | 'content';
  interestedCount?: number;
  /** When true, shows the count inside the button instead of as a separate label */
  countInline?: boolean;
  /** Interest mode: 'simple' (default toggle) or 'structured' (opens dialog) */
  interestMode?: InterestMode;
  /** Config for structured interest mode */
  structuredInterestConfig?: StructuredInterestConfig;
  /** When true, renders the button in a disabled state (e.g. user not approved) */
  disabled?: boolean;
}
export function InterestedButton(t0) {
  const $ = _c(75);
  const {
    objectId,
    objectType,
    interestedCount,
    countInline: t1,
    interestMode: t2,
    structuredInterestConfig,
    disabled: t3
  } = t0;
  const countInline = t1 === undefined ? false : t1;
  const interestMode = t2 === undefined ? "simple" : t2;
  const disabled = t3 === undefined ? false : t3;
  const {
    data: session
  } = useSession();
  let t4;
  if ($[0] !== session?.user?.id) {
    t4 = session?.user?.id?.toString();
    $[0] = session?.user?.id;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const currentUserId = t4;
  const {
    showSignInModal
  } = useSignInModal();
  const pendingActionRef = useRef(null);
  const {
    data: t5
  } = useObjectActivities(objectId, objectType);
  let t6;
  if ($[2] !== t5) {
    t6 = t5 === undefined ? [] : t5;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const activities = t6;
  const {
    data: engagementData
  } = useEngagementSummary(objectId, objectType);
  const addVote = useAddVote();
  const removeVote = useRemoveVote();
  const [dialogOpen, setDialogOpen] = useState(false);
  let t7;
  if ($[4] !== activities || $[5] !== currentUserId) {
    let t8;
    if ($[7] !== currentUserId) {
      t8 = a => a.type === "vote" && String(a.actorId) === currentUserId && !a.deletedAt && (a.data as any)?.vote === "interested";
      $[7] = currentUserId;
      $[8] = t8;
    } else {
      t8 = $[8];
    }
    t7 = activities.find(t8);
    $[4] = activities;
    $[5] = currentUserId;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const existingVoteActivity = t7;
  const isInterested = !!existingVoteActivity;
  let t8;
  if ($[9] !== existingVoteActivity?.data) {
    t8 = (existingVoteActivity?.data as any)?.entities || [];
    $[9] = existingVoteActivity?.data;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const existingEntities = t8;
  const existingNote = (existingVoteActivity?.data as any)?.note || "";
  const existingBrand = (existingVoteActivity?.data as any)?.brand || "";
  const existingTimeframe = (existingVoteActivity?.data as any)?.timeframe || "";
  let t9;
  if ($[11] !== addVote || $[12] !== currentUserId || $[13] !== interestMode || $[14] !== isInterested || $[15] !== objectId || $[16] !== objectType || $[17] !== removeVote || $[18] !== showSignInModal || $[19] !== structuredInterestConfig) {
    t9 = async () => {
      if (!currentUserId) {
        pendingActionRef.current = interestMode === "structured" ? "structured" : "simple";
        showSignInModal({
          description: <div className="font-bold p-6"><div className="text-lg">Sign in or sign up to register your interest</div><div className="text-sm">Sign up takes 20 seconds</div></div>,
          preferFullscreenForNewUsers: false,
          onSuccess: () => {
            const action = pendingActionRef.current;
            pendingActionRef.current = null;
            if (action === "structured" && structuredInterestConfig) {
              setDialogOpen(true);
            } else {
              addVote.mutate({
                objectId,
                objectType,
                vote: "interested"
              }, {
                onSuccess: _temp
              });
            }
          }
        });
        return;
      }
      if (interestMode === "structured" && structuredInterestConfig) {
        setDialogOpen(true);
      } else {
        if (isInterested) {
          await removeVote.mutateAsync({
            actorId: currentUserId,
            objectId,
            objectType
          });
          toast.success("Interest removed");
        } else {
          await addVote.mutateAsync({
            objectId,
            objectType,
            vote: "interested"
          });
          toast.success("Interest registered");
        }
      }
    };
    $[11] = addVote;
    $[12] = currentUserId;
    $[13] = interestMode;
    $[14] = isInterested;
    $[15] = objectId;
    $[16] = objectType;
    $[17] = removeVote;
    $[18] = showSignInModal;
    $[19] = structuredInterestConfig;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleToggle = t9;
  let t10;
  if ($[21] !== addVote || $[22] !== currentUserId || $[23] !== objectId || $[24] !== objectType) {
    t10 = async data => {
      if (!currentUserId) {
        return;
      }
      await addVote.mutateAsync({
        objectId,
        objectType,
        vote: "interested",
        entities: data.entities,
        note: data.note,
        brand: data.brand,
        timeframe: data.timeframe
      });
      setDialogOpen(false);
    };
    $[21] = addVote;
    $[22] = currentUserId;
    $[23] = objectId;
    $[24] = objectType;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleStructuredSubmit = t10;
  let t11;
  if ($[26] !== currentUserId || $[27] !== objectId || $[28] !== objectType || $[29] !== removeVote) {
    t11 = async () => {
      if (!currentUserId) {
        return;
      }
      await removeVote.mutateAsync({
        actorId: currentUserId,
        objectId,
        objectType
      });
      setDialogOpen(false);
    };
    $[26] = currentUserId;
    $[27] = objectId;
    $[28] = objectType;
    $[29] = removeVote;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleRemoveInterest = t11;
  if (!session?.user && disabled) {
    return null;
  }
  const isPending = addVote.isPending || removeVote.isPending;
  const isDisabled = disabled && !!session?.user || isPending;
  const displayCount = engagementData?.engagement?.votes?.interested?.count ?? interestedCount ?? 0;
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      enableEntitySelection: true,
      requireEntity: true,
      allowNote: true,
      requireNote: false,
      allowMultipleEntities: true
    };
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const defaultConfig = t12;
  const t13 = isInterested ? "bg-yellow-50 text-yellow-700 border border-yellow-300" : "bg-gray-100 text-gray-900 border border-gray-200 hover:bg-gray-150";
  const t14 = isDisabled && "opacity-50 cursor-not-allowed";
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = clsx("flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer", t13, t14);
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const t16 = isInterested && "fill-yellow-200";
  let t17;
  if ($[35] !== t16) {
    t17 = clsx("w-5 h-5", t16);
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const t18 = isInterested ? 2.5 : 2;
  let t19;
  if ($[37] !== t17 || $[38] !== t18) {
    t19 = <Hand className={t17} strokeWidth={t18} />;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span>Interested</span>;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== interestMode) {
    t21 = interestMode === "structured" && <Pencil className="w-3.5 h-3.5 text-current opacity-60" />;
    $[41] = interestMode;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== countInline || $[44] !== displayCount) {
    t22 = countInline && displayCount > 0 && <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">{displayCount}</span>;
    $[43] = countInline;
    $[44] = displayCount;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== handleToggle || $[47] !== isDisabled || $[48] !== t15 || $[49] !== t19 || $[50] !== t21 || $[51] !== t22) {
    t23 = <button onClick={handleToggle} disabled={isDisabled} className={t15}>{t19}{t20}{t21}{t22}</button>;
    $[46] = handleToggle;
    $[47] = isDisabled;
    $[48] = t15;
    $[49] = t19;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== countInline || $[54] !== displayCount) {
    t24 = !countInline && displayCount > 0 && <span className="text-sm text-gray-500">{displayCount} interested</span>;
    $[53] = countInline;
    $[54] = displayCount;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t23 || $[57] !== t24) {
    t25 = <div className="flex items-center gap-2">{t23}{t24}</div>;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== addVote.isPending || $[60] !== dialogOpen || $[61] !== existingBrand || $[62] !== existingEntities || $[63] !== existingNote || $[64] !== existingTimeframe || $[65] !== handleRemoveInterest || $[66] !== handleStructuredSubmit || $[67] !== interestMode || $[68] !== isInterested || $[69] !== removeVote || $[70] !== structuredInterestConfig) {
    t26 = interestMode === "structured" && <StructuredInterestDialog open={dialogOpen} onOpenChange={setDialogOpen} config={structuredInterestConfig || defaultConfig} onSubmit={handleStructuredSubmit} onRemove={isInterested ? handleRemoveInterest : undefined} isEditing={isInterested} initialEntities={existingEntities} initialNote={existingNote} initialBrand={existingBrand} initialTimeframe={existingTimeframe} isPending={addVote.isPending} isRemovePending={removeVote.isPending} />;
    $[59] = addVote.isPending;
    $[60] = dialogOpen;
    $[61] = existingBrand;
    $[62] = existingEntities;
    $[63] = existingNote;
    $[64] = existingTimeframe;
    $[65] = handleRemoveInterest;
    $[66] = handleStructuredSubmit;
    $[67] = interestMode;
    $[68] = isInterested;
    $[69] = removeVote;
    $[70] = structuredInterestConfig;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t25 || $[73] !== t26) {
    t27 = <>{t25}{t26}</>;
    $[72] = t25;
    $[73] = t26;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  return t27;
}
function _temp() {
  toast.success("Interest registered");
}